import { useState, useEffect } from "react";
import axios from "axios";
import { UserCog, Trash2, Plus, Loader2, ShieldCheck, UserCheck, KeyRound } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementDialog = ({ open, onOpenChange }) => {
  const { user: currentUser } = useAuth();
  const isManager = currentUser?.role === "manager";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("admin");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsers(res.data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchUsers();
  }, [open]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newUsername.trim()) {
      toast.error("Fill username and name");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("Password must be at least 8 characters (or leave blank for first-time setup)");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/auth/users`, {
        name: newName.trim(),
        username: newUsername.trim(),
        email: newEmail.trim(),
        password: newPassword,
        role: newRole,
      });
      toast.success(
        newPassword
          ? `User ${newUsername} created`
          : `User ${newUsername} created — they'll set their password on first login`
      );
      setNewName("");
      setNewUsername("");
      setNewEmail("");
      setNewPassword("");
      setNewRole("admin");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setAdding(false);
    }
  };

  const handleChangeRole = async (id, newRoleValue) => {
    try {
      await axios.patch(`${API}/auth/users/${id}/role`, { role: newRoleValue });
      toast.success("Role updated");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change role");
    }
  };

  const handleResetPassword = async (id, name) => {
    if (!window.confirm(`Reset password for ${name}? They'll be prompted to set a new password on their next login.`)) return;
    try {
      await axios.post(`${API}/auth/users/${id}/reset-password`);
      toast.success(`${name}'s password has been reset — they must set a new one on next login`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${id}`);
      toast.success("User removed");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    }
  };

  const formatDT = (iso) => {
    try {
      return format(new Date(iso), "MMM dd, yyyy");
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl border-[#E4E4E7] rounded-sm max-h-[85vh] overflow-y-auto"
        data-testid="user-management-dialog"
      >
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <UserCog className="w-6 h-6" />
            User Management
          </DialogTitle>
          <p className="text-sm text-[#71717A]">Create and manage users who can log in to the system</p>
        </DialogHeader>

        {/* Add form */}
        <form onSubmit={handleAdd} className="py-4 border-b border-[#E4E4E7] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-name" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Name
              </Label>
              <Input
                id="new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Full name"
                required
                className="border-[#E4E4E7] rounded-sm"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-username" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Username
              </Label>
              <Input
                id="new-username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="e.g. jsmith"
                required
                className="border-[#E4E4E7] rounded-sm"
                data-testid="new-user-username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-email" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Email (optional)
              </Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@company.com"
                className="border-[#E4E4E7] rounded-sm"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Initial Password <span className="font-normal normal-case text-[10px] text-[#A1A1AA]">(leave blank → user sets on first login)</span>
              </Label>
              <Input
                id="new-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Leave blank for first-time setup"
                minLength={8}
                className="border-[#E4E4E7] rounded-sm"
                data-testid="new-user-password"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="new-role" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Role
              </Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger id="new-role" className="border-[#E4E4E7] rounded-sm" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                  <SelectItem value="hr" data-testid="role-option-hr">HR</SelectItem>
                  <SelectItem value="finance" data-testid="role-option-finance">Finance</SelectItem>
                  {isManager && (
                    <SelectItem value="manager" data-testid="role-option-manager">Manager</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={adding}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm h-10"
              data-testid="add-user-btn"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create User
            </Button>
          </div>
        </form>

        {/* List */}
        <div className="py-2">
          {loading ? (
            <div className="p-6 text-center text-[#71717A]" data-testid="users-loading">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-[#71717A]" data-testid="users-empty">
              No users yet.
            </div>
          ) : (
            <div className="space-y-1" data-testid="users-list">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-sm hover:bg-[#F4F4F5] transition-colors"
                  data-testid={`user-item-${u.id}`}
                >
                  <div className="w-9 h-9 rounded-sm bg-[#0F172A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#09090B] truncate flex items-center gap-2 flex-wrap">
                      {u.name}
                      {u.id === currentUser?.id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#0F172A] text-white font-semibold">
                          You
                        </span>
                      )}
                      {u.password_set === false && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] font-bold"
                          title="Password not yet set — user must complete first-time setup"
                          data-testid={`badge-awaiting-setup-${u.id}`}
                        >
                          Awaiting setup
                        </span>
                      )}
                      {u.role === "manager" ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] font-bold"
                          data-testid={`role-badge-manager-${u.id}`}
                        >
                          <ShieldCheck className="w-3 h-3" />
                          Manager
                        </span>
                      ) : u.role === "hr" ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#E0E7FF] text-[#4F46E5] border border-[#C7D2FE] font-bold"
                          data-testid={`role-badge-hr-${u.id}`}
                        >
                          <UserCheck className="w-3 h-3" />
                          HR
                        </span>
                      ) : u.role === "finance" ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] font-bold"
                          data-testid={`role-badge-finance-${u.id}`}
                        >
                          <ShieldCheck className="w-3 h-3" />
                          Finance
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7] font-bold"
                          data-testid={`role-badge-admin-${u.id}`}
                        >
                          <ShieldCheck className="w-3 h-3" />
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#71717A] truncate">
                      {u.username ? `@${u.username}` : ""} {u.email ? `• ${u.email}` : ""}
                    </div>
                    <div className="text-[10px] text-[#71717A] mt-0.5">
                      Added {formatDT(u.created_at)}
                    </div>
                  </div>
                  {isManager && u.id !== currentUser?.id && (
                    <Select
                      value={u.role}
                      onValueChange={(v) => handleChangeRole(u.id, v)}
                    >
                      <SelectTrigger
                        className="w-28 h-8 border-[#E4E4E7] rounded-sm text-xs"
                        data-testid={`change-role-${u.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {isManager && u.id !== currentUser?.id && u.password_set !== false && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleResetPassword(u.id, u.name)}
                      title="Reset password — user will set a new one on next login"
                      className="rounded-sm hover:bg-[#FEF3C7] hover:text-[#D97706]"
                      data-testid={`reset-password-${u.id}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </Button>
                  )}
                  {u.id !== currentUser?.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(u.id, u.name)}
                      className="rounded-sm hover:bg-red-50 hover:text-red-600"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2 text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3">
          {users.length} user{users.length !== 1 ? "s" : ""} • Roles: manager > admin, hr
          {!isManager && " • (manager role required to change roles)"}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementDialog;
